import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const fadeUp = { hidden: { opacity: 0, y: 32 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25,0.46,0.45,0.94] } } };

const Stat = ({ value, label }) => (
  <div className="text-center">
    <p className="font-display font-black text-4xl md:text-5xl" style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{value}</p>
    <p className="text-[#7a7870] text-sm mt-1">{label}</p>
  </div>
);

const Feature = ({ icon, title, desc }) => (
  <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(201,168,76,0.12)' }}>
    <div className="text-2xl mb-3">{icon}</div>
    <h3 className="text-white font-display font-bold text-base mb-2">{title}</h3>
    <p className="text-[#7a7870] text-sm leading-relaxed">{desc}</p>
  </div>
);

const PlanCard = ({ name, price, sub, features, cta, highlight, onCta }) => (
  <div className="rounded-2xl p-7 flex flex-col gap-5 transition-all duration-300 hover:-translate-y-1"
    style={{
      background: highlight ? 'rgba(201,168,76,0.08)' : 'rgba(255,255,255,0.02)',
      border: highlight ? '1px solid rgba(201,168,76,0.4)' : '1px solid rgba(255,255,255,0.06)',
      boxShadow: highlight ? '0 0 60px rgba(201,168,76,0.1)' : 'none'
    }}>
    {highlight && (
      <span className="self-start text-xs font-bold text-black px-3 py-1 rounded-full" style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}>Más popular</span>
    )}
    <div>
      <p className="text-[#c9a84c] text-sm font-semibold mb-1">{name}</p>
      <p className="font-display font-black text-3xl text-white">{price} <span className="text-[#7a7870] text-sm font-normal">{sub}</span></p>
    </div>
    <ul className="space-y-2 flex-1">
      {features.map(f => (
        <li key={f} className="flex items-start gap-2 text-sm text-[#9a9890]">
          <svg className="flex-shrink-0 mt-0.5" viewBox="0 0 16 16" width="14" height="14" fill="none">
            <circle cx="8" cy="8" r="7" fill="rgba(201,168,76,0.15)"/>
            <polyline points="4.5,8 7,10.5 11.5,5.5" stroke="#c9a84c" strokeWidth="1.5"/>
          </svg>
          {f}
        </li>
      ))}
    </ul>
    <button onClick={onCta}
      className="w-full py-3 rounded-xl font-display font-bold text-sm transition-all duration-300"
      style={highlight
        ? { background: 'linear-gradient(135deg, #c9a84c, #e8c870)', color: '#000' }
        : { background: 'rgba(255,255,255,0.06)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' }}>
      {cta}
    </button>
  </div>
);

const FEATURES = [
  { icon: '🏠', title: 'CRM inmobiliario completo', desc: 'Gestiona compradores, vendedores y propiedades desde un único panel. Sin hojas de Excel, sin caos.' },
  { icon: '🤖', title: 'IA integrada', desc: 'Análisis de mercado automatizado, predicción de precios y argumentos de venta generados por IA.' },
  { icon: '🗺️', title: 'Mapas 3D interactivos', desc: 'Visualiza propiedades en mapas tridimensionales para presentaciones que impresionan a tus clientes.' },
  { icon: '📊', title: 'Estadísticas en tiempo real', desc: 'Sigue el rendimiento de tu cartera, tasas de conversión y objetivos del equipo en un vistazo.' },
  { icon: '⚡', title: 'Automatizaciones', desc: 'Crea reglas que actúan solas: notificaciones, tareas automáticas, seguimientos programados.' },
  { icon: '👥', title: 'Equipos y organizaciones', desc: 'Gestiona agentes, asigna carteras y coordina tu equipo con roles y permisos granulares.' },
];

const PLANS = [
  { name: 'Básico', price: 'Gratis', sub: 'para siempre', features: ['Hasta 10 propiedades', 'CRM básico', 'Mapas interactivos', 'Soporte por email'], cta: 'Empezar gratis', highlight: false },
  { name: 'Esencial', price: '€9,99', sub: 'al mes', features: ['Propiedades ilimitadas', 'CRM avanzado', 'Análisis IA', 'Comunidad premium', 'Base de conocimiento'], cta: 'Probar gratis 14 días', highlight: true },
  { name: 'Profesional', price: '€24,99', sub: 'al mes', features: ['Todo lo anterior', 'CRM ilimitado para equipo', 'Organizaciones y roles', 'Automatizaciones', 'Estadísticas avanzadas', 'Soporte prioritario'], cta: 'Contactar ventas', highlight: false },
];

const TESTIMONIALS = [
  { name: 'Carlos Moreno', role: 'Director, Inmobiliaria Moreno', quote: 'Pasamos de 3 herramientas distintas a solo E-HOME. El CRM de equipo nos ahorra 2 horas diarias.', avatar: 'CM' },
  { name: 'Lucía Ferrández', role: 'Agente independiente, Valencia', quote: 'El análisis de mercado con IA me da argumentos reales ante los clientes. Ha subido mis cierres un 40%.', avatar: 'LF' },
  { name: 'Grupo Reyes Inmuebles', role: 'Franquicia · 12 agentes', quote: 'Las automatizaciones y los objetivos por agente cambiaron nuestra forma de gestionar el equipo.', avatar: 'GR' },
];

export const Agentes = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: '#050508', color: '#e8e6e0' }}>
      {/* Nav */}
      <nav className="px-6 md:px-12 py-5 flex items-center justify-between border-b border-white/5">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#0d0d10', border: '1.5px solid rgba(201,168,76,0.3)' }}>
            <svg viewBox="0 0 40 40" width="24" height="24" fill="none">
              <polygon points="20,5 4,18 36,18" fill="#f0c040"/>
              <rect x="26" y="8" width="4" height="8" fill="#f0c040"/>
              <rect x="7" y="18" width="26" height="17" fill="#f0c040"/>
              <rect x="15" y="25" width="10" height="10" fill="#050508" rx="1"/>
              <circle cx="23" cy="30" r="1" fill="#c9a84c"/>
              <rect x="9" y="20" width="7" height="6" fill="#050508" rx="1"/>
              <rect x="24" y="20" width="7" height="6" fill="#050508" rx="1"/>
            </svg>
          </div>
          <span className="font-display font-bold text-white tracking-tight">E-<span className="text-[#c9a84c]">HOME</span></span>
        </Link>
        <button onClick={() => navigate('/login')}
          className="text-sm font-display font-bold text-black px-5 py-2.5 rounded-xl transition-all duration-300 hover:-translate-y-0.5"
          style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}>
          Empezar gratis →
        </button>
      </nav>

      {/* Hero */}
      <section className="px-6 md:px-12 py-24 text-center max-w-5xl mx-auto">
        <motion.div initial="hidden" animate="show" variants={fadeUp}>
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold text-[#c9a84c] mb-8 backdrop-blur-sm"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(201,168,76,0.3)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#c9a84c] animate-pulse" />
            La plataforma que usan los mejores agentes de España
          </div>
          <h1 className="font-display font-black text-5xl md:text-7xl text-white leading-tight mb-6">
            Únete a cientos de agentes<br />
            <span style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              que ya ganan más con E-HOME
            </span>
          </h1>
          <p className="text-[#7a7870] text-xl mb-10 max-w-2xl mx-auto">
            El CRM inmobiliario con IA, mapas 3D y automatizaciones que transforma cómo gestionas tu negocio. De forma totalmente gratuita.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/login')}
              className="font-display font-black text-black text-lg px-10 py-5 rounded-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_60px_rgba(201,168,76,0.5)]"
              style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}>
              Crear cuenta gratuita →
            </button>
            <Link to="/contacto"
              className="font-display font-bold text-white text-lg px-10 py-5 rounded-xl transition-all duration-300 hover:border-[#c9a84c]/50"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
              Hablar con ventas
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Stats */}
      <section className="px-6 md:px-12 py-16 border-y border-white/5">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          <Stat value="+500" label="agentes activos" />
          <Stat value="40%" label="más cierres de media" />
          <Stat value="2h" label="ahorradas al día" />
          <Stat value="4.9★" label="valoración media" />
        </div>
      </section>

      {/* Features */}
      <section className="px-6 md:px-12 py-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display font-black text-4xl md:text-5xl text-white mb-4">Todo lo que necesitas en un solo lugar</h2>
            <p className="text-[#7a7870] text-lg">Sin integraciones complicadas. Sin suscripciones extra.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(f => <Feature key={f.title} {...f} />)}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="px-6 md:px-12 py-16" style={{ background: 'rgba(255,255,255,0.01)' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display font-black text-3xl text-white text-center mb-12">Lo que dicen nuestros agentes</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map(t => (
              <div key={t.name} className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <p className="text-[#9a9890] text-sm leading-relaxed mb-5">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-black" style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}>{t.avatar}</div>
                  <div>
                    <p className="text-white text-sm font-semibold">{t.name}</p>
                    <p className="text-[#7a7870] text-xs">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="px-6 md:px-12 py-24">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display font-black text-4xl md:text-5xl text-white mb-4">Planes transparentes</h2>
            <p className="text-[#7a7870] text-lg">Empieza gratis. Crece cuando quieras.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PLANS.map(p => (
              <PlanCard key={p.name} {...p} onCta={() => navigate(p.cta === 'Contactar ventas' ? '/contacto' : '/login')} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 md:px-12 py-24 text-center" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <h2 className="font-display font-black text-4xl md:text-6xl text-white mb-4">
          Empieza hoy.<br />
          <span style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            De forma totalmente gratuita.
          </span>
        </h2>
        <p className="text-[#7a7870] text-lg mb-10">Sin tarjeta de crédito. Sin compromisos.</p>
        <button onClick={() => navigate('/login')}
          className="font-display font-black text-black text-lg px-12 py-5 rounded-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_60px_rgba(201,168,76,0.5)]"
          style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}>
          Crear cuenta gratuita →
        </button>
      </section>

      <footer className="border-t border-white/5 px-6 py-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#050508', border: '1.5px solid rgba(201,168,76,0.3)' }}>
              <svg viewBox="0 0 40 40" width="20" height="20" fill="none">
                <polygon points="20,5 4,18 36,18" fill="#f0c040"/>
                <rect x="26" y="8" width="4" height="8" fill="#f0c040"/>
                <rect x="7" y="18" width="26" height="17" fill="#f0c040"/>
                <rect x="15" y="25" width="10" height="10" fill="#050508" rx="1"/>
                <circle cx="23" cy="30" r="1" fill="#c9a84c"/>
                <rect x="9" y="20" width="7" height="6" fill="#050508" rx="1"/>
                <rect x="24" y="20" width="7" height="6" fill="#050508" rx="1"/>
              </svg>
            </div>
            <span className="font-display font-bold text-white tracking-tight">E-<span className="text-[#c9a84c]">HOME</span></span>
          </div>
          <p className="text-[#7a7870] text-xs">© 2026 ehomesp.com · Todos los derechos reservados</p>
          <div className="flex gap-6 text-[#7a7870] text-xs">
            <Link to="/privacidad" className="hover:text-[#c9a84c] transition-colors">Privacidad</Link>
            <Link to="/cookies" className="hover:text-[#c9a84c] transition-colors">Cookies</Link>
            <Link to="/contacto" className="hover:text-[#c9a84c] transition-colors">Contacto</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Agentes;
